import { View, StyleSheet } from "react-native";
import CustomText from "../../components/common/general/CustomText/CustomText";
import { useVoiceAssistant } from "@livekit/components-react";
import { ScrollView } from "react-native-gesture-handler";
import { useEffect, useState, useRef } from "react";
import { SIMPLE_VOICE_ASSISTANT_HEIGHT } from "../../constants/assessmentConstants";

interface Segment {
  text: string;
  timestamp: number;
}

interface SimpleVoiceAssistantProps {
  questionIndex: number;
}

function SimpleVoiceAssistant({ questionIndex }: SimpleVoiceAssistantProps) {
  const { agentTranscriptions } = useVoiceAssistant();
  const [displayText, setDisplayText] = useState("");
  const lastQuestionIndex = useRef(0);
  const segmentStartIndex = useRef(0);
  const lastUpdateTimeRef = useRef<number>(0);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const UPDATE_MIN_INTERVAL_MS = 80; // adjust to taste (lower = more frequent updates)

  const segments = agentTranscriptions;

  useEffect(() => {
    if (questionIndex !== lastQuestionIndex.current && questionIndex > 0) {
      lastQuestionIndex.current = questionIndex;
      segmentStartIndex.current = segments.length;
      // reset visible text for the new question
      setDisplayText("");
      // clear any pending throttled updates
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
      lastUpdateTimeRef.current = 0;
    }
  }, [questionIndex]);

  useEffect(() => {
    if (questionIndex > 0 && segments.length > segmentStartIndex.current) {
      const questionSegments: Segment[] = segments.slice(segmentStartIndex.current);
      if (questionSegments.length > 0) {
        const nextText = questionSegments.map((s) => s.text).join(" ");

        const now = Date.now();
        const elapsed = now - lastUpdateTimeRef.current;

        if (elapsed >= UPDATE_MIN_INTERVAL_MS) {
          setDisplayText(nextText);
          lastUpdateTimeRef.current = now;
        } else {
          if (pendingTimeoutRef.current) {
            clearTimeout(pendingTimeoutRef.current);
          }
          pendingTimeoutRef.current = setTimeout(() => {
            setDisplayText(nextText);
            lastUpdateTimeRef.current = Date.now();
            pendingTimeoutRef.current = null;
          }, UPDATE_MIN_INTERVAL_MS - elapsed);
        }
      }
    }

    return () => {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    };
  }, [segments, questionIndex]);

  return (
    <View style={styles.transcriptContainer}>
      <ScrollView style={styles.scrollView}>
        {displayText && (
          <CustomText
            text={displayText}
            size="xl"
            weight="normal"
            style={styles.transcriptText}
            useTranslationText={false}
          />
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  transcriptContainer: {
    flex: 1,
    padding: 20,
    justifyContent: "center",
    alignItems: "center",
    height: SIMPLE_VOICE_ASSISTANT_HEIGHT,
  },
  transcriptText: {
    width: "100%",
    maxWidth: 675,
    textAlign: "center",
    color: "#667085",
  },
  scrollView: {
    height: 100,
    width: "100%",
  },
});

export default SimpleVoiceAssistant;
